// src/hooks/useRoleFramework.js
// Fetches and parses the graded CSV role framework.
// The CSV is the single source of truth — nothing is hardcoded.

import { useState, useEffect, useMemo } from 'react';
import { COMPETENCY_GROUPS, ROLE_STYLE } from '../utils/roleFrameworkData';

const CSV_PATH = '/brio_roles_competency_matrix_graded.csv';

// 19 competency column keys in CSV order
const COMPETENCY_KEYS = [
  'drawings_2d', 'modelling_3d', 'presentation_graphics', 'concept_design',
  'detail_resolution', 'gfc_drawings', 'gfc_checking', 'cost_control',
  'time_management', 'consultant_vendor_coordination', 'site_supervision',
  'training_support_juniors', 'project_acquisition_new_clients',
  'bim_coordination', 'specification_writing', 'authority_approvals',
  'contract_administration', 'design_quality_accountability',
  'budget_programme_accountability',
];

/**
 * Parse a CSV line respecting quoted fields (fields may contain commas).
 */
function parseCSVLine(line) {
  const fields = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

function parseCSV(text) {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  const headers = parseCSVLine(lines[0]);
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const fields = parseCSVLine(lines[i]);
    if (fields.length < headers.length) continue;

    const obj = {};
    headers.forEach((h, idx) => { obj[h] = fields[idx]; });

    // Build competency map with numeric grades (0-3)
    const competencies = {};
    COMPETENCY_KEYS.forEach(key => {
      competencies[key] = parseInt(obj[key], 10) || 0;
    });

    rows.push({
      roleCode:            obj.role_code,
      roleName:            obj.role_name,
      roleSublevel:        obj.role_sublevel,
      roleSort:            parseInt(obj.role_sort, 10) || 0,
      experienceRange:     obj.experience_range,
      bonusEligible:       (obj.bonus_eligible || '').toLowerCase().startsWith('yes'),
      projectScope:        obj.project_scope,
      projectComplexity:   obj.project_complexity,
      projectLead:         (obj.project_lead || '').toLowerCase() === 'yes',
      clientInteraction:   obj.client_interaction,
      hiringInvolvement:   (obj.hiring_involvement || '').toLowerCase() === 'yes',
      hiringScope:         obj.hiring_scope || '',
      coreResponsibilities: obj.core_responsibilities,
      growthFocus:         obj.growth_focus,
      competencies,
    });
  }

  return rows.sort((a, b) => a.roleSort - b.roleSort);
}

// NL schaal → entry-level sublevel in the CSV framework.
// Each schaal maps to the starting sublevel for that role tier.
const NL_SCHAAL_SUBLEVEL = {
  '1':  { role: 'Intern',             sublevel: 'Level 0' },
  '2':  { role: 'Junior Architect',   sublevel: 'Level 1' },
  '3':  { role: 'Junior Architect',   sublevel: 'Level 3' },
  '4':  { role: 'Medior Architect',   sublevel: 'Level 2' },
  '5':  { role: 'Medior Architect',   sublevel: 'Level 5' },
  '6':  { role: 'Project Architect',  sublevel: 'Level 4' },
  '7':  { role: 'Senior Architect',   sublevel: 'Level 6' },
  '8':  { role: 'Senior Architect',   sublevel: 'Level 8' },
  '9':  { role: 'Senior Architect',   sublevel: 'Level 9' },
  '10':  { role: 'Associate', sublevel: 'Associate L10' },
  'c_1': { role: 'Partner / Director',  sublevel: 'Partner / Director' },
  'c_2': { role: 'Partner / Director',  sublevel: 'Partner / Director' },
  'c_3': { role: 'Partner / Director',  sublevel: 'Partner / Director' },
  'c_4': { role: 'Partner / Director',  sublevel: 'Partner / Director' },
};

/**
 * Match a user's jobRole + jobLevel to a framework row.
 * For NL users, nlSchaal provides the schaal number for precise matching.
 */
function matchUserToRole(rows, jobRole, jobLevel, nlSchaal) {
  if (!jobRole || !rows.length) return null;
  const rawRole = String(jobRole || '').trim();
  const level = String(jobLevel ?? '').trim();

  // NL users have roles like "Senior Architect (9)" — extract schaal and strip suffix
  // Also handle "Director 11" where the number is part of the name (no parentheses)
  const schaalMatch = rawRole.match(/\((\d{1,2})\)\s*$/);
  const effectiveSchaal = nlSchaal || (schaalMatch ? schaalMatch[1] : null);
  const strippedRole = rawRole.replace(/\s*\(\d+\)$/, '').replace(/\s+\d+$/, '');

  // DB may store short names that differ from CSV ("Partner / Director")
  const ROLE_ALIASES = { 'Director': 'Partner / Director', 'Partner': 'Partner / Director', 'Associate Architect': 'Associate' };
  const role = ROLE_ALIASES[strippedRole] || strippedRole;

  // NL schaal-based matching — use the schaal→sublevel map
  if (effectiveSchaal) {
    const mapping = NL_SCHAAL_SUBLEVEL[String(effectiveSchaal)];
    if (mapping) {
      const schaalRow = rows.find(r => r.roleName === mapping.role && r.roleSublevel === mapping.sublevel);
      if (schaalRow) return schaalRow;
    }
  }

  // Non-NL level-based matching
  if (level && level !== '0') {
    // Try exact match first (e.g. roleSublevel === "Level 5")
    const exact = rows.find(r => r.roleName === role && r.roleSublevel === level);
    if (exact) return exact;

    // DB stores level as a number (e.g. 5); CSV has "Level 5" or "Partner"
    const prefixed = `Level ${level}`;
    const prefixedMatch = rows.find(r => r.roleName === role && r.roleSublevel === prefixed);
    if (prefixedMatch) return prefixedMatch;
  }
  return rows.find(r => r.roleName === role) || null;
}

/**
 * Return prev/next rows adjacent to the given row.
 */
function getAdjacentRows(rows, currentRow) {
  if (!currentRow || !rows.length) return { prev: null, next: null };
  const idx = rows.findIndex(r => r.roleSort === currentRow.roleSort);
  return {
    prev: idx > 0 ? rows[idx - 1] : null,
    next: idx < rows.length - 1 ? rows[idx + 1] : null,
  };
}

/**
 * Main hook — fetches CSV once, parses and caches.
 */
export default function useRoleFramework() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(CSV_PATH)
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load role framework CSV (${res.status})`);
        return res.text();
      })
      .then(text => {
        if (cancelled) return;
        setRawRows(parseCSV(text));
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Grouped by role code
  const roleGroups = useMemo(() => {
    const codes = [...new Set(rawRows.map(r => r.roleCode))];
    return codes.map(code => ({
      roleCode: code,
      roleName: rawRows.find(r => r.roleCode === code)?.roleName || code,
      rows: rawRows.filter(r => r.roleCode === code),
    }));
  }, [rawRows]);

  return {
    rows: rawRows,
    roleGroups,
    competencyGroups: COMPETENCY_GROUPS,
    roleStyle: ROLE_STYLE,
    loading,
    error,
    matchUserToRole: (jobRole, jobLevel, nlSchaal) => matchUserToRole(rawRows, jobRole, jobLevel, nlSchaal),
    getAdjacentRows: (currentRow) => getAdjacentRows(rawRows, currentRow),
  };
}
